import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../utils/apiClient';
import {
  Users, TrendingUp, Ban, Mail, MailOpen, UserCheck, UserX,
  BarChart, PieChart, Activity, Calendar, AlertCircle, ArrowLeft, Lock
} from 'lucide-react';

interface MemberStatistics {
  total_members: number;
  active_members_30days: number;
  dormant_members: number;
  gender_breakdown: {
    male: number;
    female: number;
    other: number;
    unknown: number;
    male_percent: number;
    female_percent: number;
    other_percent: number;
    unknown_percent: number;
  };
  blocked_count: number;
  block_rate: number;
  unblock_count_this_month: number;
  block_count_this_month: number;
  recovery_rate: number;
  messages_sent: number;
  messages_read: number;
  overall_read_rate: number;
  messages_sent_this_month: number;
  messages_read_this_month: number;
  monthly_read_rate: number;
  avg_visit_count: number;
  members_with_points: number;
  members_with_coupons: number;
  weekly_visitors: number;
  monthly_visitors: number;
  active_rate: number;
  dormant_rate: number;
  weekly_visit_rate: number;
  monthly_visit_rate: number;
}

// TODO: 将来的にfeature flagやDB設定でアンロック判定に切り替え
const isUnlocked = false;

const MemberStatisticsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<MemberStatistics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isUnlocked) {
      fetchStatistics();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchStatistics = async () => {
    try {
      const response = await apiClient.get('/member-statistics');
      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('統計取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!isUnlocked) {
    return (
      <div style={{ padding: '24px' }}>
        {/* プレミアムオプション案内 */}
        <div style={{
          background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
          borderRadius: 16,
          padding: '48px 32px',
          color: '#fff',
          textAlign: 'center',
          marginBottom: 24,
          boxShadow: '0 20px 60px rgba(17, 153, 142, 0.3)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F4CA;</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 8px 0', letterSpacing: 1 }}>
            プレミアムオプション
          </h1>
          <h2 style={{ fontSize: 20, fontWeight: 400, margin: '0 0 32px 0', opacity: 0.9 }}>
            会員統計ダッシュボード
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 16,
            maxWidth: 800,
            margin: '0 auto 32px',
            textAlign: 'left'
          }}>
            {[
              { icon: '\u{1F465}', title: '会員基本統計', desc: '総会員数・アクティブ会員・休眠会員をリアルタイム表示' },
              { icon: '\u{1F4CA}', title: '男女比分析', desc: '性別ごとの会員構成比をグラフ表示' },
              { icon: '\u{1F6AB}', title: 'ブロック統計', desc: 'ブロック数・今月ブロック/復帰・復帰率を分析' },
              { icon: '\u{1F4C5}', title: '来店統計', desc: '週次/月次来店数・平均来店回数・ポイント保有状況' },
              { icon: '\u{1F4E9}', title: 'メッセージ開封率', desc: '配信メッセージの開封率を全期間・今月別に分析' },
              { icon: '\u{1F4C8}', title: 'エンゲージメント分析', desc: '会員の活動状況を多角的に把握しリテンション向上' },
            ].map((feature, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.15)',
                borderRadius: 12,
                padding: '16px 20px',
                backdropFilter: 'blur(10px)',
              }}>
                <div style={{ fontSize: 24, marginBottom: 8 }}>{feature.icon}</div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{feature.title}</div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.5 }}>{feature.desc}</div>
              </div>
            ))}
          </div>

          <div style={{
            display: 'inline-block',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 8,
            padding: '12px 32px',
            fontSize: 14,
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.3)',
          }}>
            &#x1F6A7; 開発検討中 &#x2015; ご契約・お問い合わせは開発担当まで
          </div>
        </div>

        {/* 検収前初期設定期間（緑枠） */}
        <div style={{
          background: '#f6ffed',
          border: '2px solid #52c41a',
          borderRadius: 8,
          padding: '16px 24px',
          marginBottom: 24,
          maxWidth: 500
        }}>
          <p style={{ fontWeight: 700, color: '#389e0d', marginBottom: 8, fontSize: 14 }}>
            &#x1F4C5; 検収前初期設定期間
          </p>
          <p style={{ color: '#595959', fontSize: 13, margin: 0, lineHeight: 1.8 }}>
            <strong>2月25日まで</strong>：検収前初期設定期間につき、統計設定の変更は<strong style={{ color: '#389e0d' }}>無償</strong>にて対応いたします。<br />
            <strong>2月26日以降</strong>：別途お見積りさせていただきます。
          </p>
        </div>

        {/* ぼかしプレビュー */}
        <div style={{ position: 'relative' }}>
          <div style={{
            filter: 'blur(5px)',
            opacity: 0.5,
            pointerEvents: 'none',
            userSelect: 'none',
          }}>
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">会員統計ダッシュボード</h2>
              </div>

              {/* ダミー基本統計 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">総会員数</p>
                      <p className="text-3xl font-bold text-gray-900">--</p>
                    </div>
                    <Users className="w-12 h-12 text-blue-600" />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">アクティブ会員 (30日)</p>
                      <p className="text-3xl font-bold text-green-600">--</p>
                    </div>
                    <UserCheck className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">休眠会員</p>
                      <p className="text-3xl font-bold text-orange-600">--</p>
                    </div>
                    <UserX className="w-12 h-12 text-orange-600" />
                  </div>
                </div>
              </div>

              {/* ダミー男女比 */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <PieChart className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-semibold text-gray-900">男女比</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['男性', '女性', 'その他', '未設定'].map((label, i) => (
                    <div key={i} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{label}</p>
                      <p className="text-2xl font-bold text-gray-400">--</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* ダミーブロック統計 */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Ban className="w-5 h-5 text-red-600" />
                  <h3 className="text-lg font-semibold text-gray-900">ブロック統計</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['ブロック中', '今月ブロック', '今月復帰', '復帰率'].map((label, i) => (
                    <div key={i} className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">{label}</p>
                      <p className="text-2xl font-bold text-gray-400">--</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ロックオーバーレイ */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 10,
          }}>
            <Lock style={{ width: 48, height: 48, color: '#8c8c8c', opacity: 0.6 }} />
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">統計データを読み込めませんでした</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">会員統計ダッシュボード</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/line')}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 flex items-center gap-2"
              title="会員一覧に戻る"
            >
              <ArrowLeft className="w-4 h-4" />
              戻る
            </button>
            <button
              onClick={fetchStatistics}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Activity className="w-4 h-4" />
              更新
            </button>
          </div>
        </div>

      {/* 基本統計 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">総会員数</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total_members}</p>
            </div>
            <Users className="w-12 h-12 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">アクティブ会員 (30日)</p>
              <p className="text-3xl font-bold text-green-600">{stats.active_members_30days}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.active_rate}%</p>
            </div>
            <UserCheck className="w-12 h-12 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">休眠会員</p>
              <p className="text-3xl font-bold text-orange-600">{stats.dormant_members}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.dormant_rate}%</p>
            </div>
            <UserX className="w-12 h-12 text-orange-600" />
          </div>
        </div>
      </div>

      {/* 男女比 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <PieChart className="w-5 h-5 text-gray-700" />
          <h3 className="text-lg font-semibold text-gray-900">男女比</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">男性</p>
            <p className="text-2xl font-bold text-blue-600">{stats.gender_breakdown.male}</p>
            <p className="text-xs text-gray-500">{stats.gender_breakdown.male_percent}%</p>
          </div>
          <div className="text-center p-4 bg-pink-50 rounded-lg">
            <p className="text-sm text-gray-600">女性</p>
            <p className="text-2xl font-bold text-pink-600">{stats.gender_breakdown.female}</p>
            <p className="text-xs text-gray-500">{stats.gender_breakdown.female_percent}%</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600">その他</p>
            <p className="text-2xl font-bold text-purple-600">{stats.gender_breakdown.other}</p>
            <p className="text-xs text-gray-500">{stats.gender_breakdown.other_percent}%</p>
          </div>
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">未設定</p>
            <p className="text-2xl font-bold text-gray-600">{stats.gender_breakdown.unknown}</p>
            <p className="text-xs text-gray-500">{stats.gender_breakdown.unknown_percent}%</p>
          </div>
        </div>
      </div>

      {/* ブロック統計 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Ban className="w-5 h-5 text-red-600" />
          <h3 className="text-lg font-semibold text-gray-900">ブロック統計</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-gray-600">ブロック中</p>
            <p className="text-2xl font-bold text-red-600">{stats.blocked_count}</p>
            <p className="text-xs text-gray-500">{stats.block_rate}%</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600">今月ブロック</p>
            <p className="text-2xl font-bold text-orange-600">{stats.block_count_this_month}</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600">今月復帰</p>
            <p className="text-2xl font-bold text-green-600">{stats.unblock_count_this_month}</p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">復帰率</p>
            <p className="text-2xl font-bold text-blue-600">{stats.recovery_rate}%</p>
          </div>
        </div>
      </div>


      {/* 来店統計 */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-semibold text-gray-900">来店統計</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-gray-600">週次来店</p>
            <p className="text-2xl font-bold text-blue-600">{stats.weekly_visitors}</p>
            <p className="text-xs text-gray-500">{stats.weekly_visit_rate}%</p>
          </div>
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <p className="text-sm text-gray-600">月次来店</p>
            <p className="text-2xl font-bold text-green-600">{stats.monthly_visitors}</p>
            <p className="text-xs text-gray-500">{stats.monthly_visit_rate}%</p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-gray-600">平均来店回数</p>
            <p className="text-2xl font-bold text-purple-600">{stats.avg_visit_count}</p>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-gray-600">ポイント保有</p>
            <p className="text-2xl font-bold text-orange-600">{stats.members_with_points}</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MemberStatisticsDashboard;
